import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { api, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Lock, Camera, CheckCircle2, Upload, RefreshCw, AlertTriangle, FileDown, ScanLine, HelpCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

const SCAN_REGION = "claim-code-scan-region";

/**
 * B2B panel: partner verifies the owner's claim code and uploads a pre-shipment
 * photo of the object. Used in Registry (restitution) and Shipments (shipping).
 */
export default function ClaimCodePanel({ declId, requirePhoto = false, onChange, verificationQuestions = [] }) {
  const { t } = useLang();
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [photoThumb, setPhotoThumb] = useState(null);

  const load = useCallback(async () => {
    if (!declId) return;
    try { setStatus(await api(`/claim/${declId}/status`)); }
    catch { setStatus(null); }
  }, [declId]);

  useEffect(() => { load(); }, [load]);

  // Load an authenticated thumbnail of the pre-shipment photo (the photo endpoint
  // requires a Bearer token, so an <img src> can't fetch it directly).
  useEffect(() => {
    let revoked = null;
    if (declId && status?.has_photo) {
      (async () => {
        try {
          const res = await fetch(`${API}/claim/${declId}/photo`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          if (!res.ok) return;
          const blob = await res.blob();
          revoked = URL.createObjectURL(blob);
          setPhotoThumb(revoked);
        } catch { /* ignore */ }
      })();
    } else {
      setPhotoThumb(null);
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [declId, status?.has_photo, status?.photo_source]);

  const verifyWith = useCallback(async (raw) => {
    const c = (raw || "").trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    try {
      await api(`/claim/${declId}/verify`, { method: "POST", body: { code: c } });
      toast.success(t("hc.claimCodeVerified"));
      setCode("");
      await load();
      onChange?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }, [declId, load, onChange]);

  function verify() { verifyWith(code); }

  async function regenerate() {
    setRegenerating(true);
    try {
      await api(`/claim/${declId}/regenerate`, { method: "POST" });
      toast.success(t("hc.newCodeGeneratedAndSent2"));
      await load();
      onChange?.();
    } catch (e) { toast.error(e.message); } finally { setRegenerating(false); }
  }

  async function downloadReceipt() {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/claim/${declId}/restitution-receipt.pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(j.detail || `Erreur ${res.status}`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `findloop-preuve-restitution-${declId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); } finally { setDownloading(false); }
  }

  async function upload(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API}/claim/${declId}/photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Erreur ${res.status}`);
      }
      toast.success(t("hc.itemPhotoSaved"));
      await load();
      onChange?.();
    } catch (e) { toast.error(e.message); } finally { setUploading(false); }
  }

  if (!status) return null;
  if (!status.has_code && !requirePhoto) return null;

  return (
    <div className="rounded-xl border border-findo-indigo/20 bg-findo-indigo/[0.04] p-4 space-y-3" data-testid="claim-code-panel">
      <p className="text-sm font-bold text-findo-indigo flex items-center gap-1.5">
        <ShieldCheck size={16} /> {t("hc.returnSecurity")}
      </p>

      {Array.isArray(verificationQuestions) && verificationQuestions.filter((q) => (q || "").trim()).length > 0 && (
        <div className="rounded-lg border border-indigo-200 bg-white/70 px-3 py-2.5" data-testid="restitution-verification-questions">
          <p className="text-[11px] font-bold uppercase tracking-wide text-indigo-700 flex items-center gap-1.5">
            <HelpCircle size={13} /> {t("hc.questionsToAskTheOwner")}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">{t("hc.askThemAtTheCounter")}</p>
          <ol className="mt-1.5 space-y-1 list-decimal list-inside">
            {verificationQuestions.filter((q) => (q || "").trim()).map((q, i) => (
              <li key={i} className="text-sm text-slate-800" data-testid={`restitution-verification-question-${i}`}>{q}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Claim code */}
      {status.has_code && (
        status.verified ? (
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="claim-code-verified">
            <CheckCircle2 size={16} /> {t("hc.claimCodeVerified")}
          </div>
        ) : status.expired ? (
          <div className="space-y-2" data-testid="claim-code-expired-block">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
              <AlertTriangle size={16} /> {t("hc.claimCodeExpired")}
            </div>
            <Button onClick={regenerate} disabled={regenerating} className="rounded-xl font-semibold" data-testid="claim-code-regenerate-btn">
              <RefreshCw size={15} className="mr-1.5" /> {regenerating ? "…" : t("hc.regenerateAndResendToThe")}
            </Button>
          </div>
        ) : (
          <div data-testid="claim-code-verify-block">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
              <Lock size={13} /> {t("hc.ownerSClaimCode")}
            </label>
            <div className="flex gap-2 mt-1.5">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={16}
                placeholder={t("hc.exCode")} className="h-10 rounded-xl font-mono uppercase" data-testid="claim-code-input" />
              <Button onClick={verify} disabled={busy || !code.trim()} className="rounded-xl font-semibold" data-testid="claim-code-verify-btn">
                {t("hc.verify")}
              </Button>
            </div>
            <button type="button" onClick={() => setScanOpen(true)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-findo-indigo hover:underline" data-testid="claim-code-scan-btn">
              <ScanLine size={14} /> {t("hc.scanTheOwnerSQr")}
            </button>
            <p className="text-[11px] text-slate-400 mt-1">{t("hc.theOwnerReceivedItBy")}</p>
            <ClaimScanDialog open={scanOpen} onClose={() => setScanOpen(false)} onResult={(d) => { setScanOpen(false); verifyWith(d); }} />
          </div>
        )
      )}

      {/* Object photo */}
      {requirePhoto && (
        <div data-testid="claim-photo-block">
          {status.has_photo ? (
            <div className="space-y-1.5" data-testid="claim-photo-done">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <CheckCircle2 size={16} />
                {status.photo_source === "item_original" ? t("hc.originalListingPhotoUsed") : t("hc.itemPhotoSaved")}
              </div>
              {status.photo_source === "item_original" && (
                <p className="text-[11px] text-slate-400">{t("hc.preFilledAutomaticallyYouCan")}</p>
              )}
              {photoThumb && (
                <img src={photoThumb} alt={t("hc.itemPreview")}
                  className="h-24 w-24 object-cover rounded-xl border border-border shadow-sm" data-testid="claim-photo-preview" />
              )}
              <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-findo-indigo cursor-pointer hover:underline" data-testid="claim-photo-change-label">
                <Camera size={13} /> {uploading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("hc.sending")}</span> : t("hc.takeChangeThePhoto")}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} disabled={uploading} className="hidden" data-testid="claim-photo-input" />
              </label>
            </div>
          ) : (
            <div>
              <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
                <Camera size={13} /> {t("hc.itemPhotoRequiredBeforeShipping")}
              </label>
              <label className="mt-1.5 inline-flex items-center gap-2 h-10 px-4 rounded-xl border border-dashed border-findo-indigo/40 text-sm font-semibold text-findo-indigo cursor-pointer hover:bg-findo-indigo/5" data-testid="claim-photo-upload-label">
                <Upload size={15} /> {uploading ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />{t("hc.sending")}</span> : t("hc.addAPhoto")}
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} disabled={uploading} className="hidden" data-testid="claim-photo-input" />
              </label>
              <p className="text-[11px] text-slate-400 mt-1">{t("hc.visibleToTheOwnerAutomatically")}</p>
            </div>
          )}
        </div>
      )}

      {status.restituted && (
        <div className="border-t border-findo-indigo/15 pt-3" data-testid="claim-receipt-block">
          <Button variant="outline" onClick={downloadReceipt} disabled={downloading}
            className="rounded-xl font-semibold border-findo-indigo/30 text-findo-indigo hover:bg-findo-indigo/5" data-testid="claim-receipt-btn">
            <FileDown size={15} className="mr-1.5" /> {downloading ? <FindoSpinner size={16} /> : t("hc.proofOfReturnPdf")}
          </Button>
        </div>
      )}
    </div>
  );
}

function ClaimScanDialog({ open, onClose, onResult }) {
  const { t } = useLang();
  const scannerRef = useRef(null);
  const resultRef = useRef(onResult);
  resultRef.current = onResult;
  const [camState, setCamState] = useState("starting");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let scanner = null;
    setCamState("starting");

    const stopSafely = async () => {
      const s = scanner;
      scanner = null;
      scannerRef.current = null;
      if (!s) return;
      try {
        const st = typeof s.getState === "function" ? s.getState() : null;
        if (st === Html5QrcodeScannerState.SCANNING || st === Html5QrcodeScannerState.PAUSED) {
          await s.stop();
        }
      } catch (e) { /* scanner already stopped — ignore */ }
      try { s.clear(); } catch (e) { /* ignore */ }
    };

    // Defer one tick so the dialog's portal content is mounted before
    // html5-qrcode looks up the scan region (otherwise it throws synchronously).
    const timer = setTimeout(() => {
      if (cancelled) return;
      try {
        scanner = new Html5Qrcode(SCAN_REGION, { verbose: false });
        scannerRef.current = scanner;
        scanner
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: { width: 220, height: 220 } },
            (decoded) => {
              if (cancelled) return;
              cancelled = true;
              stopSafely().finally(() => resultRef.current?.(decoded));
            },
            () => {},
          )
          .then(() => { if (!cancelled) setCamState("running"); })
          .catch(() => { if (!cancelled) setCamState("error"); });
      } catch (e) {
        setCamState("error");
      }
    }, 60);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      stopSafely();
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="claim-scan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><ScanLine size={20} className="text-findo-indigo" /> {t("hc.scanTheClaimCode")}</DialogTitle></DialogHeader>
        <div className="rounded-xl overflow-hidden bg-slate-900 relative" style={{ minHeight: 220 }}>
          <div id={SCAN_REGION} className="w-full" />
          {camState === "error" && <p className="absolute inset-0 grid place-items-center text-white/80 text-sm p-6 text-center">{t("hc.cameraUnavailableEnterTheCode")}</p>}
        </div>
        <p className="text-xs text-slate-400 mt-2">{t("hc.askTheOwnerToDisplay")}</p>
      </DialogContent>
    </Dialog>
  );
}
